import React, { useState } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

function ValproicCalculator({user}) {
    const [ageGroup, setAgeGroup] = useState('adult');
    const [therapyType, setTherapyType] = useState('mono');
    const [weightNum, setWeightNum] = useState ('');
    const [dose, setDose] = useState(''); // Default 1000mg/day
    const [measuredLevel, setMeasuredLevel] = useState('');
    const [simDose, setSimDose] = useState ('');

    // === 1. POPULATION CONSTANTS LOOKUP ===
    const VPA_CONSTANTS = {
        adult: {
            mono: { min: 7, max: 12 },
            poly: { min: 15, max: 18 }
        },
        pediatric: {
            mono: { min: 10, max: 20 },
            poly: { min: 20, max: 30 }
        }
    };

    // === 2. EXTRACT CONTEXT PARAMETERS ===
    const selected = VPA_CONSTANTS[ageGroup][therapyType];
    const clMin = (selected.min * Number(weightNum)) / 1000;
    const clMax = (selected.max * Number(weightNum)) / 1000;
    const vdTotal = 0.15 * Number(weightNum);

    const keMin = vdTotal > 0 ? clMin / vdTotal : 0;
    const keMax = vdTotal > 0 ? clMax / vdTotal : 0;

    const thalfMin = keMax > 0 ? 0.693 / keMax : 0; 
    const thalfMax = keMin > 0 ? 0.693 / keMin : 0; 

    // === 3. STEP 3 & 4: POPULATION TROUGH EXPECTATION VALUES ===
    const currentDoseNum = Number(dose);
    const cssMin = clMax > 0 ? currentDoseNum / (clMax * 24) : 0; // Max CL results in Min Css
    const cssMax = clMin > 0 ? currentDoseNum / (clMin * 24) : 0; // Min CL results in Max Css
    
    // Fixes 'actual', 'midPredicted', and 'percentDev' not defined errors!
    const actual = Number(measuredLevel);
    const midPredicted = (cssMin + cssMax) / 2;
    const percentDev = midPredicted > 0 ? ((actual - midPredicted) / midPredicted) * 100 : 0;

    // === 4. PROFILE CLINICAL CORRELATION DEVIATION INTERPRETATION ===
    let comparisonResult = {
        text: "Within Range",
        color: "#2ecc71",
        note: "Patient is responding as expected for an average adult/pediatric profile."
    };

    if (measuredLevel && actual < cssMin) {
        comparisonResult = {
            text: "Below Predicted Range",
            color: "#3498db",
            note: `Result is ${Math.abs(percentDev).toFixed(0)}% lower than expected from mid Range. Check compliance or induction.`
        };
    } else if (measuredLevel && actual > cssMax) {
        comparisonResult = {
            text: "Above Predicted Range",
            color: "#e67e22",
            note: `Result is ${percentDev.toFixed(0)}% higher than expected from mid Range. Check for slow metabolism or inhibition.`
        };
    }

    // === 5. NEW STEP 5: RE-ENGINEERED HIGH-PRECISION INDIVIDUAL PINPOINT MODE ===
    const simDoseNum = Number(simDose);
    let simCssPinpoint = 0;

    // Direct high-precision individual scaling ratio 
    if (simDoseNum > 0 && currentDoseNum > 0 && actual > 0) {
        simCssPinpoint = (simDoseNum / currentDoseNum) * actual;
    }

    // === 6. STEP 6: CLINICAL TARGET INTERVENTION ROUTING ===
    let recColor = "#2ecc71"; 
    let recTitle = "THERAPEUTIC";
    let recAction = "";

    if (actual < 50) {
        recColor = "#3498db"; 
        recTitle = "SUBTHERAPEUTIC";
        recAction = simDoseNum > 0 
            ? `If response is poor but compliance is good, increase dose up to ${simDose} mg/day and re-sample. If response is good, continue current dose.`
            : "Level is sub-therapeutic (< 50 mcg/mL). If response is poor, check compliance first before considering a dose increment. If response is good, continue current dose.";
    } else if (actual >= 50 && actual <= 100) {
        recColor = "#2ecc71"; 
        recTitle = "THERAPEUTIC";
        recAction = "Level within therapeutic range (50-100 mcg/mL). If response is poor but compliance good, consider a small dose increment (do not exceed max). If response is good, continue current dose.";
    } else if (actual > 100) {
        recColor = "#e74c3c"; 
        recTitle = "POTENTIAL TOXICITY";
        recAction = "Withhold dose and monitor for CNS side effects (tremors, lethargy). Review dose once level falls.";
    }

    // === 7. CLINICAL ENZYME ENHANCEMENT INSIGHTS ===
    const getVPAClinicalInsight = () => {
        if (!measuredLevel || !midPredicted) return null;
        
        if (actual > midPredicted * 1.2) { 
            return {
                title: "Actual > Population Estimate",
                reason: "Patient may be a 'Slow Metabolizer' or taking an inhibitor (e.g. Cimetidine, Chlorpromazine, Felbamate, Topiramate).",
                color: "#e67e22"
            };
        } else if (actual < midPredicted * 0.8) { 
            return {
                title: "Actual < Population Estimate",
                reason: "Patient may be a 'Fast Metabolizer', have poor absorption, or a compliance issue. Also consider induction by other drugs (Carbamazepine, Carbapenem, Lamotrigine, Phenytoin, Phenobarbitone, Primidone, Rifampicin).",
                color: "#3498db"
            };
        }
        return {
            title: "Matches Population Estimate",
            reason: "Patient's metabolism is consistent with standard population parameters.",
            color: "#2ecc71"
        };
    };
    
    const insight = getVPAClinicalInsight();

    return (
        <section style={{ padding: '15px', background: '#1e272e', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                <button 
                    type="button"
                    className="no-print" 
                    onClick={() => {
                                    const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                                    const trackingLabel = `${actualUserEmail}-printed-Valproic`;
                                    logUserActivityQuietly(trackingLabel); 
                                    window.print();
                                }} 
                    style={{
                    padding: '10px 20px',
                    backgroundColor: '#27ae60', 
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
                    }}
                >
                    🖨️ Print PDF Report
                </button>
            </div>
            <h2 style={{ textAlign: 'center', color: '#3498db', marginTop: 0 }}>Valproate TDM Worksheet</h2>
            <details style={{
                backgroundColor: '#1E293B',
                border: '1px solid #38BDF8',
                borderRadius: '8px',
                padding: '10px 15px',
                marginBottom: '20px',
                cursor: 'pointer'
            }}>
                <summary style={{ color: '#38BDF8', fontWeight: 'bold' }}>
                ⏱️ Check Clinical Sampling Protocol (Click to expand)
                </summary>
                <div style={{ marginTop: '12px', padding: '15px', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', border: '1px solid #3d4e5d', fontSize: '0.9rem', color: '#F8FAFC' }}>
                    
                    {/* SECTION 1: TIMING & STEADY STATE MANAGEMENT */}
                    <div style={{ marginBottom: '15px' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#00d2ff', textTransform: 'uppercase', display: 'block', marginBottom: '6px', letterSpacing: '0.5px' }}>
                            ⏱️ Sampling Timeline & Steady State
                        </span>
                        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                            <tbody>
                                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#fff', width: '35%' }}>Initial Monitoring:</td>
                                    <td style={{ padding: '6px 0', color: '#bdc3c7' }}>Draw steady-state levels after **2 – 4 days** of continuous therapy.</td>
                                </tr>
                                <tr>
                                    <td style={{ padding: '6px 0', fontWeight: 'bold', color: '#fff' }}>Pre-Sample Draw:</td>
                                    <td style={{ padding: '6px 0', color: '#bdc3c7' }}>
                                        Draw **30 mins prior** or immediately before the next scheduled dose (Oral/IV Trough).
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* SECTION 2: INDICATION METRIC GRID */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '15px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                        {/* Seizure Target Card */}
                        <div style={{ background: 'rgba(52, 152, 219, 0.08)', border: '1px solid rgba(52, 152, 219, 0.2)', padding: '10px', borderRadius: '6px' }}>
                            <div style={{ fontWeight: 'bold', color: '#3498db', fontSize: '0.8rem', textTransform: 'uppercase' }}>⚡ Seizures / Epilepsy:</div>
                            <div style={{ fontSize: '1.15rem', fontWeight: 'bold', marginTop: '4px', color: '#fff' }}>
                                50 - 100 <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#bdc3c7' }}>mg/L</span>
                            </div>
                        </div>

                        {/* Psychiatric Target Card */}
                        <div style={{ background: 'rgba(155, 89, 182, 0.08)', border: '1px solid rgba(155, 89, 182, 0.2)', padding: '10px', borderRadius: '6px' }}>
                            <div style={{ fontWeight: 'bold', color: '#9b59b6', fontSize: '0.8rem', textTransform: 'uppercase' }}>🧠 Psychiatric Disorders:</div>
                            <div style={{ fontSize: '1.15rem', fontWeight: 'bold', marginTop: '4px', color: '#fff' }}>
                                50 - 125 <span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#bdc3c7' }}>mg/L</span>
                            </div>
                        </div>
                    </div>

                    {/* SECTION 3: CRITICAL TOXICITY ALERTS */}
                    <div style={{ background: 'rgba(231, 76, 60, 0.08)', borderLeft: '4px solid #e74c3c', padding: '10px', borderRadius: '0 6px 6px 0' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e74c3c', textTransform: 'uppercase', display: 'block' }}>
                            ⚠️ Critical Toxicity Thresholds
                        </span>
                        <div style={{ fontSize: '0.85rem', color: '#ecf0f1', marginTop: '4px', lineHeight: '1.4' }}>
                            • General Toxicity risk increases when trough levels exceed <strong>&gt; 120 mg/L</strong>.<br />
                            • <strong style={{ color: '#ff7675' }}>Severe CNS Toxicity / Somnolence</strong> is highly correlated with concentrations <strong>&gt; 175 mg/L</strong>.
                        </div>
                    </div>

                </div>
            </details>
                <section style={{ marginBottom: '20px', padding: '15px', background: '#1e272e', borderRadius: '10px' }}>
                    <h3 style={{ color: '#3498db', marginTop: 0 }}>Step 1: Patient Profile</h3>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Age Group:</label>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                            <button onClick={() => setAgeGroup('adult')} style={{ ...btnStyle, background: ageGroup === 'adult' ? '#3498db' : '#34495e', flex: 1 }}>Adult</button>
                            <button onClick={() => setAgeGroup('pediatric')} style={{ ...btnStyle, background: ageGroup === 'pediatric' ? '#3498db' : '#34495e', flex: 1 }}>Pediatric</button>
                        </div>
                    </div>
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ fontSize: '0.8rem', color: '#bdc3c7', display: 'block', marginBottom: '5px' }}>
                            Actual Body Weight (kg):
                        </label>
                        <input 
                            type="number" 
                            value={weightNum} 
                            onChange={(e) => {const val = e.target.value; setWeightNum(val === "" ? "" : Number(val))}} 
                            placeholder="e.g. 70"
                            style={{
                                width: '80%',
                                padding: '10px',
                                background: '#2c3e50',
                                border: '1px solid #34495e',
                                borderRadius: '5px',
                                color: 'white',
                                fontSize: '1rem'
                            }}
                        />
                    </div>

                    <div>
                        <label style={{ fontSize: '0.8rem', color: '#bdc3c7' }}>Therapy Type:</label>
                        <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                            <button onClick={() => setTherapyType('mono')} style={{ ...btnStyle, background: therapyType === 'mono' ? '#2ecc71' : '#34495e', flex: 1 }}>Monotherapy</button>
                            <button onClick={() => setTherapyType('poly')} style={{ ...btnStyle, background: therapyType === 'poly' ? '#e67e22' : '#34495e', flex: 1 }}>Polytherapy</button>
                        </div>
                    </div>
                </section>
                <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#3498db' }}>Step 2: Kinetic Ranges (Predicted)</h4>
                    
                    <table style={{ width: '100%', fontSize: '0.9rem', color: '#ecf0f1', borderCollapse: 'collapse' }}>
                        <tbody>
                            {/* CL RANGE */}
                            <tr style={{ borderBottom: '1px solid #34495e' }}>
                                <td style={{ padding: '10px 0' }}>
                                    <strong>CL Range:</strong>
                                    <div style={{ fontSize: '0.75rem', color: '#95a5a6', marginTop: '4px' }}>
                                        Formula: (Constant × {weightNum}kg) / 1000
                                    </div>
                                    <div style={{ fontSize: '0.7rem', color: '#3498db', fontStyle: 'italic' }}>
                                        Constants used: Min={selected.min}, Max={selected.max} ml/kg/hr
                                    </div>
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', verticalAlign: 'top', paddingTop: '10px' }}>
                                    {clMin.toFixed(2)} - {clMax.toFixed(2)} L/hr
                                </td>
                            </tr>
                            {/* KE RANGE */}
                            <tr style={{ borderBottom: '1px solid #34495e' }}>
                                <td style={{ padding: '10px 0' }}>
                                    <strong>Ke Range:</strong>
                                    <div style={{ fontSize: '0.75rem', color: '#95a5a6', marginTop: '4px' }}>
                                        Formula: CL / Vd
                                    </div>
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', verticalAlign: 'top', paddingTop: '10px' }}>
                                    {keMin.toFixed(3)} - {keMax.toFixed(3)} hr⁻¹
                                </td>
                            </tr>
                            {/* HALF LIFE RANGE */}
                            <tr>
                                <td style={{ padding: '10px 0' }}>
                                    <strong> T½ (Half-life):</strong>
                                    <div style={{ fontSize: '0.75rem', color: '#95a5a6', marginTop: '4px' }}>
                                        Formula: 0.693 / Ke
                                    </div>
                                </td>
                                <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#2ecc71', verticalAlign: 'top', paddingTop: '10px' }}>
                                    {thalfMin.toFixed(1)} - {thalfMax.toFixed(1)} hours
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div style={{ marginTop: '10px', fontSize: '0.75rem', color: '#95a5a6', fontStyle: 'italic' }}>
                    *Calculated using Vd: {vdTotal.toFixed(1)} L ({0.15} L/kg)
                </div>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ fontSize: '0.8rem', color: '#bdc3c7', display: 'block', marginTop: '5px' }}>
                        Current Total Daily Dose (mg/day):
                    </label>
                    <input 
                        type="number" 
                        value={dose} 
                        onChange={(e) => {const val = e.target.value; setDose(val === "" ? "" : Number(val))}}
                        style={{
                            width: '80%',
                            padding: '10px',
                            background: '#2c3e50',
                            border: '1px solid #3498db', // Blue border to highlight this input
                            borderRadius: '5px',
                            color: 'white',
                            fontSize: '1rem'
                        }}
                    /> <span style={{color: 'white', fontSize: '1rem'}} > mg </span>
                </div>
                <div style={{ marginTop: '20px', padding: '15px', background: '#2c3e50', borderRadius: '8px', borderLeft: '5px solid #3498db' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#3498db' }}>Step 3: Predicted Steady-State </h4>
                    
                    <div style={{ fontSize: '0.9rem', color: '#ecf0f1' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <span>Predicted Range:</span>
                            <span style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#f1c40f' }}>
                                {cssMin.toFixed(1)} - {cssMax.toFixed(1)} mg/L
                            </span>
                        </div>
                        
                        <div style={{ fontSize: '0.75rem', color: '#95a5a6', borderTop: '1px solid #34495e', paddingTop: '8px' }}>
                            Formula Predict Css: Dose * S * F/ (CL × 24), S=1 F=1
                            <br />
                            Estimate Css: {dose}mg / (Range {clMin.toFixed(2)}-{clMax.toFixed(2)} × 24)
                        </div>
                    </div>
                </div>
            
            <h4 style={{ margin: '0 0 15px 0', color: '#ecf0f1' }}>Step 4: Clinical Recommendation</h4>
                <div>
                    <label> Lab Measured Level (mg/L):</label>
                </div>
                <input 
                    type="number" 
                    value={measuredLevel} 
                    onChange={(e) => {const val = e.target.value; setMeasuredLevel(val === "" ? "" : Number(val))}}
                    style={{
                        width: '80%',
                        padding: '10px',
                        background: '#2c3e50',
                        border: '1px solid #3498db', // Blue border to highlight this input
                        borderRadius: '5px',
                        color: 'white',
                        fontSize: '1rem'
                    }}
                /> <span style={{color: 'white', fontSize: '1rem'}} > mg/L </span>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <div style={{ flex: 1, textAlign: 'center', borderRight: '1px solid #34495e' }}>
                    <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>PREDICTED RANGE</div>
                    <div style={{ color: 'white', fontWeight: 'bold' }}>{cssMin.toFixed(1)} - {cssMax.toFixed(1)}</div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', color: '#95a5a6' }}>ACTUAL LEVEL</div>
                    <div style={{ fontWeight: 'bold', color: comparisonResult.color }}>{actual.toFixed(1)}</div>
                </div>
            </div>

            <div style={{ padding: '10px', background: 'rgba(0,0,0,0.2)', borderRadius: '5px', borderLeft: `4px solid ${comparisonResult.color}` , marginTop: '20px'}}>
                <strong style={{ color: comparisonResult.color }}>{comparisonResult.text}</strong>
                <p style={{ margin: '5px 0 0 0', fontSize: '0.85rem', color: '#bdc3c7' }}>{comparisonResult.note}</p>
            </div>

            {/* STEP 5: SIMULATED DOSE FORECAST (INDIVIDUAL HIGH-PRECISION MODEL) */}
            <h4 style={{ margin: '0 0 10px 0', color: '#2ecc71', fontSize: '1.1rem', marginTop: '20px' }}>
                Step 5: Simulated Dose Forecast (Individual Patient Model)
            </h4>
            <label style={{ fontSize: '0.8rem', color: '#bdc3c7', display: 'block', marginBottom: '5px' }}>
                Proposed New Dose (mg/day):
            </label>
            <input 
                type="number" 
                value={simDose} 
                onChange={(e) => setSimDose(e.target.value)} 
                placeholder="Enter proposed target trial dose..."
                style={{ width: '100%', padding: '10px', background: '#1b1f23', border: simDose ? '1px solid #2ecc71' : '1px solid #34495e', color: 'white', marginBottom: '15px', borderRadius: '6px', boxSizing: 'border-box' }} 
                disabled={!measuredLevel || !dose}
            />

            {simDoseNum > 0 && (
                <div style={{ padding: '15px', background: '#111416', borderRadius: '8px', border: '1px solid #2c3539', marginBottom: '15px' }}>
                    
                    {/* VALUE TELEMETRY PINPOINT STRING */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <span style={{ color: '#95a5a6', fontSize: '0.72rem', fontWeight: 'bold' }}>PREDICTED PATIENT STEADY STATE (Css):</span>
                        <strong style={{ fontWeight: 'bold', fontSize: '1.4rem', color: '#00d2ff' }}>
                            {simCssPinpoint.toFixed(1)} <span style={{ fontSize: '0.85rem', fontWeight: 'normal', color: '#95a5a6' }}>mg/L</span>
                        </strong>
                    </div>
                    
                    {/* VISUAL SPECTRUM SLIDER ROAD TRACK BAR */}
                    <div style={{ marginTop: '25px', height: '14px', background: '#2c3e50', borderRadius: '7px', position: 'relative', overflow: 'visible', marginBottom: '15px' }}>
                        
                        {/* TARGET THERAPEUTIC REGION SHADOW BOUNDARY HIGHLIGHT (50 to 100 mg/L Highlight Track Zone) */}
                        {/* 50mg/L maps to ~33% width on a 150mg/L total scale context */}
                        <div style={{ 
                            position: 'absolute', 
                            left: '33.3%', 
                            width: '33.3%', 
                            height: '100%', 
                            background: 'rgba(46, 204, 113, 0.22)', 
                            borderLeft: '1px dashed #2ecc71', 
                            borderRight: '1px dashed #2ecc71',
                            borderRadius: '4px'
                        }} />

                        {/* ACTIVE PATIENT PINPOINT INDICATOR NODE (A sleek pulsing glowing bead indicator) */}
                        {/* Calculated dynamically on a 0 - 150 mg/L spectrum landscape */}
                        <div style={{ 
                            position: 'absolute', 
                            left: `${Math.max(0, Math.min(98, (simCssPinpoint / 150) * 100))}%`, 
                            transform: 'translateX(-50%)',
                            width: '18px', 
                            height: '18px', 
                            top: '-2px',
                            background: '#f1c40f',
                            borderRadius: '50%',
                            boxShadow: '0 0 12px #f1c40f',
                            border: '2px solid #fff',
                            transition: 'left 0.2s ease-out',
                            zIndex: 10
                        }} />
                    </div>

                    {/* GRAPH SCALING X-AXIS SCALE FOOTER LABELS */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.62rem', color: '#7f8c8d', padding: '0 2px', marginBottom: '15px' }}>
                        <span>0 mg/L</span>
                        <span style={{ color: '#2ecc71', fontWeight: 'bold' }}>Target Spec: 50 - 100 mg/L</span>
                        <span>150 mg/L</span>
                    </div>

                    {/* CLEAR INDIVIDUAL RATIO PROOF TRACE */}
                    <div style={{ fontSize: '0.68rem', color: '#57606f', borderTop: '1px solid rgba(255,255,255,0.03)', paddingTop: '8px', lineHeight: '1.4' }}>
                        Formula Predict Css: (Proposed New Dose / Current Daily Dose) × Actual Measured Lab Level
                        <br />
                        Individual Patient Linear Ratio Trace: ({simDose} mg / {dose} mg) × {measuredLevel} mg/L = {simCssPinpoint.toFixed(1)} mg/L
                    </div>
                </div>
            )}

            {/* CLINICAL INTERPRETATION AND CARE PLAN WRAPPER BOX */}
            <div style={{ 
                marginTop: '20px', 
                padding: '15px', 
                background: '#1b1f23', 
                borderRadius: '8px', 
                borderLeft: `6px solid ${recColor}`,
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)' 
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h4 style={{ margin: 0, color: recColor, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.9rem' }}>
                        Final Recommendation
                    </h4>
                    <span style={{ fontSize: '0.8rem', color: '#95a5a6' }}>Status: {recTitle}</span>
                </div>

                <div style={{ fontSize: '0.95rem', lineHeight: '1.5', color: '#ecf0f1' }}>
                    <strong>Plan:</strong> {recAction}
                </div>

                <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #34495e', fontSize: '0.8rem', color: '#bdc3c7', fontStyle: 'italic' }}>
                    <strong>Clinical Note:</strong> {comparisonResult.note}
                </div>
            </div>

            {insight && (
                <div style={{ 
                    marginTop: '15px', 
                    padding: '12px', 
                    background: '#1b1f23', 
                    borderRadius: '8px', 
                    borderLeft: `5px solid ${insight.color}` 
                }}>
                    <h5 style={{ margin: '0 0 5px 0', color: insight.color, fontSize: '0.9rem' }}>
                        🔍 Interpretation: {insight.title}
                    </h5>
                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.4' }}>
                        {insight.reason}
                    </p>
                </div>
            )}

            {/* HANDBOOK DISCLAIMER LEGEND FOOTER FOR MEDICAL FILING */}
            <p style={{ margin: '15px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
                *Evaluation derived from official Clinical Pharmacokinetics Handbook criteria. Patient-specific clinical conditions and absolute biomarker trends must always override automated population estimates prior to final intervention execution.
            </p>
        </section>
        
        
    );
}

const btnStyle = (active) => ({
    padding: '8px 12px',
    border: 'none',
    borderRadius: '4px',
    background: active ? '#f1c40f' : '#34495e',
    color: 'white',
    cursor: 'pointer',
    flex: 1
});

export default ValproicCalculator;